import { getCategoryDisplayLabel } from '@/utils/categoryTaxonomy';

function safeDecodeRouteParam(value) {
  if (typeof value !== "string" || value.length === 0) return "";

  let decoded = value;
  for (let i = 0; i < 2; i += 1) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    } catch (_) {
      break;
    }
  }

  return decoded;
}

const PUBLIC_CATEGORY_TYPE_ALIASES = Object.freeze({
  emotion: "feeling",
});

const INTERNAL_CATEGORY_QUERY_KEYS = Object.freeze({
  feeling: "emotion",
});

const CATEGORY_ROUTE_CONFIG = Object.freeze({
  price: {
    "under-1000": {
      routeValue: "[0,999]",
      label: "Under $1000",
    },
    "1000-5000": {
      routeValue: "[1000,5000]",
      label: "$1,000 - $5,000",
    },
    "5001-10000": {
      routeValue: "[5001,10000]",
      label: "$5,001 - $10,000",
    },
    "over-10000": {
      routeValue: "[10001,9007199254740991]",
      label: "Over $10,000",
    },
  },
  size: {
    small: {
      routeValue: JSON.stringify({
        unit: "cm",
        minWidth: "0",
        minHeight: "0",
        width: "47",
        height: "47",
        condition: "smaller",
      }),
      label: "Small",
    },
    medium: {
      routeValue: JSON.stringify({
        unit: "cm",
        minWidth: "47",
        minHeight: "47",
        width: "90",
        height: "90",
        condition: "smaller",
      }),
      label: "Medium",
    },
    large: {
      routeValue: JSON.stringify({
        unit: "cm",
        minWidth: "90",
        minHeight: "90",
        width: "180",
        height: "180",
        condition: "smaller",
      }),
      label: "Large",
    },
    huge: {
      routeValue: JSON.stringify({
        unit: "cm",
        width: "180",
        height: "180",
        condition: "bigger",
      }),
      label: "Huge",
    },
  },
});

function normalizeCategoryRouteToken(value) {
  return safeDecodeRouteParam(value).trim().toLowerCase();
}

function findCategoryRouteEntry(searchType, rawValue) {
  const typeConfig = CATEGORY_ROUTE_CONFIG[resolveCategoryQueryKey(searchType)];
  if (!typeConfig) return null;

  const normalized = normalizeCategoryRouteToken(rawValue);
  if (!normalized) return null;

  const directMatch = typeConfig[normalized];
  if (directMatch) {
    return { slug: normalized, ...directMatch };
  }

  const legacyMatch = Object.entries(typeConfig).find(([, entry]) => {
    return normalizeCategoryRouteToken(entry.routeValue) === normalized;
  });
  if (!legacyMatch) {
    return null;
  }

  const [slug, entry] = legacyMatch;
  return { slug, ...entry };
}

function resolveCategoryUrlSegment(category = {}, collectionType = "") {
  const slug = typeof category?.slug === "string" ? category.slug.trim() : "";
  const routeEntry = findCategoryRouteEntry(collectionType, slug || category?.metadata?.routeValue);
  if (routeEntry?.slug) {
    return routeEntry.slug;
  }

  if (slug) {
    return collectionType === "price" || collectionType === "size"
      ? normalizeCategoryRouteToken(slug)
      : slug.toLowerCase();
  }

  const metadataRouteValue = category?.metadata?.routeValue;
  if (typeof metadataRouteValue === "string" && metadataRouteValue.trim()) {
    return normalizeCategoryRouteToken(metadataRouteValue);
  }

  return "";
}

export function resolvePublicCategoryType(collectionType = "") {
  const normalized = safeDecodeRouteParam(collectionType).trim().toLowerCase();
  return PUBLIC_CATEGORY_TYPE_ALIASES[normalized] || normalized;
}

export function resolveCategoryQueryKey(searchType = "") {
  const normalized = safeDecodeRouteParam(searchType).trim().toLowerCase();
  return INTERNAL_CATEGORY_QUERY_KEYS[normalized] || normalized;
}

export function resolveCategoryRouteValue(category = {}, collectionType = "") {
  const routeEntry = findCategoryRouteEntry(
    resolveCategoryQueryKey(collectionType),
    category?.metadata?.routeValue || category?.slug
  );
  if (routeEntry?.routeValue) {
    return routeEntry.routeValue;
  }

  const metadataRouteValue = category?.metadata?.routeValue;
  if (typeof metadataRouteValue === "string" && metadataRouteValue.trim()) {
    return metadataRouteValue.trim();
  }

  const slug = typeof category?.slug === "string" ? category.slug.trim() : "";
  if (!slug) return "";

  if (collectionType === "price" || collectionType === "size") {
    return safeDecodeRouteParam(slug);
  }

  return slug.toLowerCase();
}

export function buildCategoryDestination(collectionType = "", category = {}) {
  const routeSlug = resolveCategoryUrlSegment(category, collectionType);
  const publicType = resolvePublicCategoryType(collectionType);
  return `/category/${encodeURIComponent(publicType)}/${encodeURIComponent(routeSlug)}`;
}

export function resolveCategoryCollectionLabel(searchType, rawSearchItem, locale) {
  if (!rawSearchItem) return "";

  const internalType = resolveCategoryQueryKey(searchType);
  const routeEntry = findCategoryRouteEntry(internalType, rawSearchItem);
  if (routeEntry?.label) {
    return getCategoryDisplayLabel(internalType, routeEntry.label, locale);
  }

  const decoded = safeDecodeRouteParam(rawSearchItem);

  if (internalType === "price") {
    const normalized = decoded.replace(/\s+/g, "");
    if (normalized === "[0,999]") return getCategoryDisplayLabel("price", "Under $1000", locale);
    if (normalized === "[1000,5000]") return getCategoryDisplayLabel("price", "$1,000 - $5,000", locale);
    if (normalized === "[5001,10000]") return getCategoryDisplayLabel("price", "$5,001 - $10,000", locale);
    if (normalized === "[10001,9007199254740991]") return getCategoryDisplayLabel("price", "Over $10,000", locale);
    return getCategoryDisplayLabel("price", decoded, locale);
  }

  if (internalType === "size") {
    try {
      const parsed = JSON.parse(decoded);
      const normalized = {
        unit: parsed?.unit,
        condition: parsed?.condition,
        width: String(parsed?.width ?? ""),
        height: String(parsed?.height ?? ""),
      };

      if (normalized.condition === "smaller" && normalized.width === "47" && normalized.height === "47" && normalized.unit === "cm") return getCategoryDisplayLabel("size", "Small", locale);
      if (normalized.condition === "smaller" && normalized.width === "90" && normalized.height === "90" && normalized.unit === "cm") return getCategoryDisplayLabel("size", "Medium", locale);
      if (normalized.condition === "smaller" && normalized.width === "180" && normalized.height === "180" && normalized.unit === "cm") return getCategoryDisplayLabel("size", "Large", locale);
      if (normalized.condition === "bigger" && normalized.width === "180" && normalized.height === "180" && normalized.unit === "cm") return getCategoryDisplayLabel("size", "Huge", locale);
      if (normalized.condition === "smaller" && normalized.width === "18" && normalized.height === "18" && normalized.unit === "inches") return getCategoryDisplayLabel("size", "Small", locale);
      if (normalized.condition === "smaller" && normalized.width === "35.4" && normalized.height === "35.4" && normalized.unit === "inches") return getCategoryDisplayLabel("size", "Medium", locale);
      if (normalized.condition === "smaller" && normalized.width === "70.9" && normalized.height === "70.9" && normalized.unit === "inches") return getCategoryDisplayLabel("size", "Large", locale);
      if (normalized.condition === "bigger" && normalized.width === "70.9" && normalized.height === "70.9" && normalized.unit === "inches") return getCategoryDisplayLabel("size", "Huge", locale);
    } catch (_) {
      return getCategoryDisplayLabel("size", "Size", locale);
    }

    return getCategoryDisplayLabel("size", "Size", locale);
  }

  return getCategoryDisplayLabel(internalType, decoded.replace(/-/g, " "), locale);
}

export function resolveCategoryAdditionalParams(searchType, rawSearchItem) {
  const internalType = resolveCategoryQueryKey(searchType);
  const routeEntry = findCategoryRouteEntry(internalType, rawSearchItem);
  if (routeEntry?.routeValue) {
    return { [internalType]: routeEntry.routeValue };
  }

  const routeValue = safeDecodeRouteParam(rawSearchItem);
  if (!internalType || !routeValue) return {};
  return { [internalType]: routeValue };
}

export { safeDecodeRouteParam };
